<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const mmApiEnabled = ref(props.inbox.provider_config?.mm_api_enabled || false);
const maxPriceEnabled = ref(
  props.inbox.provider_config?.max_price_enabled || false
);
const isTogglingMmApi = ref(false);
const isTogglingMaxPrice = ref(false);

const status = ref(null);
const isLoadingStatus = ref(false);
const statusError = ref('');

watch(
  () => props.inbox.provider_config?.mm_api_enabled,
  val => {
    mmApiEnabled.value = val || false;
  }
);
watch(
  () => props.inbox.provider_config?.max_price_enabled,
  val => {
    maxPriceEnabled.value = val || false;
  }
);

const updateProviderConfig = async config => {
  await store.dispatch('inboxes/updateInbox', {
    id: props.inbox.id,
    formData: false,
    channel: {
      provider_config: {
        ...props.inbox.provider_config,
        ...config,
      },
    },
  });
};

const handleMmApiToggle = async newValue => {
  if (isTogglingMmApi.value) return;
  const previousValue = mmApiEnabled.value;
  mmApiEnabled.value = newValue;
  isTogglingMmApi.value = true;
  try {
    await updateProviderConfig({ mm_api_enabled: newValue });
    useAlert(
      newValue
        ? t('INBOX_MGMT.MARKETING_API.MM_API.ENABLED_ALERT')
        : t('INBOX_MGMT.MARKETING_API.MM_API.DISABLED_ALERT')
    );
  } catch (_) {
    mmApiEnabled.value = previousValue;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isTogglingMmApi.value = false;
  }
};

const handleMaxPriceToggle = async newValue => {
  if (isTogglingMaxPrice.value) return;
  const previousValue = maxPriceEnabled.value;
  maxPriceEnabled.value = newValue;
  isTogglingMaxPrice.value = true;
  try {
    await updateProviderConfig({ max_price_enabled: newValue });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (_) {
    maxPriceEnabled.value = previousValue;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isTogglingMaxPrice.value = false;
  }
};

const fetchStatus = async () => {
  isLoadingStatus.value = true;
  statusError.value = '';
  try {
    const { data } = await InboxesAPI.getWabaMaxPriceStatus(props.inbox.id);
    status.value = data;
  } catch (error) {
    statusError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.ERROR');
  } finally {
    isLoadingStatus.value = false;
  }
};

onMounted(fetchStatus);

const agreementLabel = computed(() => {
  const agreement = status.value?.agreement;
  if (!agreement) return t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.UNKNOWN');
  if (agreement.error)
    return t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.UNAVAILABLE');
  const key = `INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.AGREEMENT_${(
    agreement.status || 'none'
  ).toUpperCase()}`;
  return t(key);
});

const agreementAccepted = computed(
  () => status.value?.agreement?.status === 'accepted'
);

const onboardingStatus = computed(
  () =>
    status.value?.onboarding_status ||
    status.value?.lite_api_status ||
    t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.UNKNOWN')
);

const isWabaOnboarded = computed(() =>
  ['ONBOARDED'].includes(
    status.value?.onboarding_status || status.value?.lite_api_status
  )
);

const managerVisibility = computed(() => {
  const visibility = status.value?.manager_visibility;
  if (!visibility || visibility.error)
    return t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.UNAVAILABLE');
  return visibility.status;
});

const statusRows = computed(() => [
  {
    key: 'waba',
    label: t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.WABA'),
    value: status.value?.waba_id || '-',
    positive: false,
  },
  {
    key: 'onboarding',
    label: t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.ONBOARDING'),
    value: onboardingStatus.value,
    positive: isWabaOnboarded.value,
  },
  {
    key: 'agreement',
    label: t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.AGREEMENT'),
    value: agreementLabel.value,
    positive: agreementAccepted.value,
  },
  {
    key: 'visibility',
    label: t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.VISIBILITY'),
    value: managerVisibility.value,
    positive: false,
  },
]);
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Marketing Messages API -->
    <div
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingMmApi }"
    >
      <SettingsToggleSection
        :model-value="mmApiEnabled"
        :header="$t('INBOX_MGMT.MARKETING_API.MM_API.TOGGLE_LABEL')"
        :description="$t('INBOX_MGMT.MARKETING_API.MM_API.TOGGLE_DESCRIPTION')"
        :hide-toggle="isTogglingMmApi"
        @update:model-value="handleMmApiToggle"
      >
        <template v-if="isTogglingMmApi" #hiddenToggle>
          <Spinner class="size-4 text-n-slate-11" />
        </template>
      </SettingsToggleSection>
    </div>
    <p class="text-xs text-n-slate-11">
      {{ $t('INBOX_MGMT.MARKETING_API.MM_API.NOTE') }}
    </p>

    <!-- Max Price (Beta) -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <div class="flex items-center gap-2">
        <h4 class="text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.MARKETING_API.MAX_PRICE.TITLE') }}
        </h4>
        <Label :label="$t('GENERAL.BETA')" color="blue" compact />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.MARKETING_API.MAX_PRICE.DESCRIPTION') }}
      </p>

      <!-- Enrollment status card -->
      <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.TITLE') }}
          </span>
          <Button
            :label="$t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.REFRESH')"
            icon="i-lucide-refresh-cw"
            size="xs"
            variant="ghost"
            color-scheme="secondary"
            :is-loading="isLoadingStatus"
            @click="fetchStatus"
          />
        </div>
        <div
          v-if="isLoadingStatus && !status"
          class="flex items-center justify-center py-6"
        >
          <Spinner class="size-5 text-n-slate-11" />
        </div>
        <p v-else-if="statusError" class="text-sm text-n-ruby-11">
          {{ statusError }}
        </p>
        <div v-else-if="status" class="flex flex-col gap-2">
          <div
            v-for="row in statusRows"
            :key="row.key"
            class="flex items-center justify-between gap-4"
          >
            <span class="text-sm text-n-slate-11">{{ row.label }}</span>
            <span
              class="text-sm font-medium"
              :class="row.positive ? 'text-n-teal-11' : 'text-n-slate-12'"
            >
              {{ row.value }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 mt-2">
            {{ $t('INBOX_MGMT.MARKETING_API.MAX_PRICE.STATUS.HINT') }}
          </p>
        </div>
      </div>

      <div
        class="relative"
        :class="{
          'pointer-events-none opacity-60': isTogglingMaxPrice || !mmApiEnabled,
        }"
      >
        <SettingsToggleSection
          :model-value="maxPriceEnabled"
          :header="$t('INBOX_MGMT.MARKETING_API.MAX_PRICE.TOGGLE_LABEL')"
          :description="
            $t('INBOX_MGMT.MARKETING_API.MAX_PRICE.TOGGLE_DESCRIPTION')
          "
          :hide-toggle="isTogglingMaxPrice"
          @update:model-value="handleMaxPriceToggle"
        >
          <template v-if="isTogglingMaxPrice" #hiddenToggle>
            <Spinner class="size-4 text-n-slate-11" />
          </template>
        </SettingsToggleSection>
      </div>
      <p v-if="!mmApiEnabled" class="text-xs text-n-amber-11">
        {{ $t('INBOX_MGMT.MARKETING_API.MAX_PRICE.REQUIRES_MM_API') }}
      </p>
    </div>
  </div>
</template>
